import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
} from 'reactstrap';

import SupplierService from 'src/modules/3rd-party-management/apis/common/SupplierService';

const RemoveFlagConfirmModal = ({
    isOpen,
    pending,
    onCancel,
    onConfirm,
    t,
}) => (
    <Modal isOpen={isOpen} toggle={onCancel}>
        <ModalHeader toggle={onCancel}>
            {t('You are removing flag')}
        </ModalHeader>
        <ModalBody>
            {`${t('Do you like to remove this company from the monitoring system')}?`}
        </ModalBody>
        <ModalFooter>
            <Button
                disabled={pending}
                onClick={onCancel}
                color="secondary"
                outline
            >
                {t('No')}
            </Button>
            <Button
                disabled={pending}
                onClick={onConfirm}
                color="primary"
            >
                {pending ? (
                    <>
                        <Spinner
                            animation="border"
                            variant="white"
                            size="sm"
                            className="me-1"
                        />
                        {`${t('Updating')}...`}
                    </>
                ) : t('Yes')}
            </Button>
        </ModalFooter>
    </Modal>
);

const useSupplierFlagToggle = ({
    t,
    onUpdated,
}) => {
    const [supplierToUnflag, setSupplierToUnflag] = useState(null);

    const mutation = useMutation({
        mutationFn: async (supplier) => {
            const service = SupplierService.getInstance();

            return await service.toggleFlag(supplier.id);
        },
        onSuccess: ({ flag }, supplier) => {
            onUpdated?.(supplier, flag);
            setSupplierToUnflag(null);
            toast(t('The flag status changed successfully'), {
                type: 'success',
            });
        },
        onError: () => {
            toast(t('An error occurred while toggling flag status.'), {
                type: 'error',
            });
        },
    });

    const requestToggle = (supplier, isActive) => {
        if (isActive) {
            setSupplierToUnflag(supplier);
            return;
        }

        mutation.mutate(supplier);
    };

    const pendingSupplierId = mutation.isLoading
        ? mutation.variables?.id
        : null;

    return {
        requestToggle,
        pendingSupplierId,
        confirmModal: (
            <RemoveFlagConfirmModal
                isOpen={!!supplierToUnflag}
                pending={mutation.isLoading}
                onCancel={() => {
                    if (!mutation.isLoading) {
                        setSupplierToUnflag(null);
                    }
                }}
                onConfirm={() => mutation.mutate(supplierToUnflag)}
                t={t}
            />
        ),
    };
};

export default useSupplierFlagToggle;
